import React, { useCallback, useState } from 'react';
import { Box, Button, EmptyState } from '@meddihub/ui';
import {
	HostPatient,
	MzcrTemporaryStorage,
	PickedHealthDocument,
	TemporaryStorageView,
} from '@meddihub/mzcr-ui';
import { getMzcrClient } from '../Services/mzcrClient';
import { useMzcrContext } from '../Services/useMzcrContext';
import useMzcrSettingsLink from '../Services/useMzcrSettingsLink';
import useRefreshMzcrAcl from '../Services/useRefreshMzcrAcl';
import useMzcrLocale from '../Services/useMzcrLocale';
import MzcrHostPatientSearch from './MzcrHostPatientSearch';
import PickFromHealthDocumentationModal from './PickFromHealthDocumentationModal';
import { CloudOffIcon } from './icons';
// @ts-ignore
import styles from './MzcrTemporaryStoragePage.module.scss';
import useTranslation from '../../../Hooks/useTranslation';
import ContentSkeleton from '../../Base/Components/ContentSkeleton';

interface Props {
	view: TemporaryStorageView;
	/** URLs of the sub-pages – MD renders them as the left menu items and the top tabs. */
	links: Record<TemporaryStorageView, string>;
	/** Name of the current workplace – "Zásilky adresované celému pracovišti Ambulance". */
	workplaceName?: string;
}

const renderHostPatientSearch = ({
	onSelect,
}: {
	onSelect: (patient: HostPatient) => void;
}) => <MzcrHostPatientSearch onSelect={onSelect} />;

/**
 * Dočasné úložiště MZČR of the whole workplace (left menu, designs G1–G4) – packages of all
 * patients with a patient filter, and a new package for any patient.
 */
const MzcrTemporaryStoragePage: React.FC<Props> = ({
	view,
	links,
	workplaceName,
}) => {
	const { t } = useTranslation();
	const { onOpenCertificateSettings } = useMzcrSettingsLink();
	const refreshMzcrAcl = useRefreshMzcrAcl();
	const locale = useMzcrLocale();
	const mzcrContext = useMzcrContext();
	// "Přidat ze zdravotnické dokumentace" of the patient picked from eKartotéka (design G4)
	const [pickRequest, setPickRequest] = useState<{
		patientId: string;
		resolve: (documents: PickedHealthDocument[] | null) => void;
	} | null>(null);
	const onPickFromHealthDocumentation = useCallback(
		(patient?: HostPatient) =>
			new Promise<PickedHealthDocument[] | null>(resolve => {
				if (!patient?.hostPatientId) {
					resolve(null);
					return;
				}
				setPickRequest({ patientId: patient.hostPatientId, resolve });
			}),
		[],
	);
	// The sub-pages are separate MD pages (menu + top tabs) - moving between them navigates
	const changeView = (next: TemporaryStorageView) =>
		window.location.assign(links[next]);

	if (mzcrContext.isLoading) {
		return <ContentSkeleton />;
	}

	if (mzcrContext.isError || !mzcrContext.context) {
		return (
			<Box p={8}>
				<EmptyState
					tone='danger'
					icon={<CloudOffIcon />}
					title={t('component.mzcr.connectionFailed.title')}
					actions={
						<Button
							variant='link'
							onClick={mzcrContext.refetch}
							isLoading={mzcrContext.isRefetching}>
							{t('component.mzcr.retry')}
						</Button>
					}>
					{t('component.mzcr.connectionFailed.text')}
				</EmptyState>
			</Box>
		);
	}

	return (
		<div className={styles.panel}>
			<MzcrTemporaryStorage
				mzcrClient={getMzcrClient()}
				{...mzcrContext.context}
				organization={{ ...mzcrContext.context.organization, workplaceName }}
				view={view}
				onViewChange={changeView}
				renderHostPatientSearch={renderHostPatientSearch}
				onPickFromHealthDocumentation={onPickFromHealthDocumentation}
				onOpenCertificateSettings={onOpenCertificateSettings}
				locale={locale}
				onCertificateUploaded={refreshMzcrAcl}
			/>
			{pickRequest && (
				<PickFromHealthDocumentationModal
					patientId={pickRequest.patientId}
					onClose={documents => {
						pickRequest.resolve(documents);
						setPickRequest(null);
					}}
				/>
			)}
		</div>
	);
};

export default MzcrTemporaryStoragePage;
